import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import {
  Check,
  ChevronsUpDown,
  Database,
  History,
  Loader2,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';

import { Link } from 'react-router-dom';
import { useRebuildStatus, useTriggerRebuild } from '@/hooks/use-rebuild';
import { SCOPE_LABEL, formatRelativeTime } from '@/lib/rebuild-labels';
import { useRefreshFundamentals } from '@/hooks/use-refresh-fundamentals';
import { useSubscriptions, useFullHistoryScan } from '@/hooks/use-subscriptions';
import { useToast } from '@/hooks/use-toast';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import type {
  RebuildScope,
  RebuildStatusResponse,
  RebuildJob,
  RebuildLockInfo,
  RebuildAsset,
} from '@/types/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTtl(seconds: number | null): string {
  if (seconds == null || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.ceil(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

function findLock(locks: RebuildLockInfo[] | undefined, scope: RebuildScope) {
  return locks?.find((l) => l.scope === scope);
}

function findCurrent(current: RebuildJob[] | undefined, scope: RebuildScope) {
  return current?.find((j) => j.rebuildType === scope);
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface RebuildButtonProps {
  scope: RebuildScope;
  status: RebuildStatusResponse | undefined;
  isPending: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}

function RebuildButton({ scope, status, isPending, disabled, onClick, label }: RebuildButtonProps) {
  const lock = findLock(status?.locks, scope);
  const current = findCurrent(status?.current, scope);

  // Hierarchy of disable reasons — most informative first.
  if (current) {
    return (
      <Button disabled className="w-full sm:w-auto">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Running… {typeof current.progress === 'number' && current.progress > 0 ? `${current.progress}%` : ''}
      </Button>
    );
  }
  if (lock?.held) {
    return (
      <Button disabled variant="outline" className="w-full sm:w-auto">
        <Clock className="h-4 w-4 mr-2" />
        Next available in {formatTtl(lock.ttlSeconds)}
      </Button>
    );
  }
  if (isPending) {
    return (
      <Button disabled className="w-full sm:w-auto">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Starting…
      </Button>
    );
  }
  return (
    <Button onClick={onClick} disabled={disabled} className="w-full sm:w-auto">
      <RotateCcw className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}

// One entry per unique symbol — all IDs for that symbol (across accounts) bundled together.
interface SymbolGroup {
  symbol: string;
  ids: number[];
  categoryName: string | null;
  currency: string;
  accountCount: number;
}

interface AssetPickerProps {
  items: RebuildAsset[];
  value: string | null; // selected symbol key
  onChange: (symbol: string | null, ids: number[]) => void;
}

function AssetPicker({ items, value, onChange }: AssetPickerProps) {
  const [open, setOpen] = useState(false);

  // Group items by symbol — the same ticker across multiple accounts
  // should appear once in the picker. Selecting it rebuilds all accounts.
  const symbolGroups = useMemo<SymbolGroup[]>(() => {
    const map = new Map<string, SymbolGroup>();
    for (const item of items) {
      const key = item.symbol;
      if (map.has(key)) {
        map.get(key)!.ids.push(item.id);
        map.get(key)!.accountCount += 1;
      } else {
        map.set(key, {
          symbol: item.symbol,
          ids: [item.id],
          categoryName: item.category?.name ?? null,
          currency: item.currency,
          accountCount: 1,
        });
      }
    }
    return [...map.values()].sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [items]);

  const selected = useMemo(
    () => symbolGroups.find((g) => g.symbol === value) ?? null,
    [symbolGroups, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selected ? (
            <span className="truncate">
              <span className="font-medium">{selected.symbol}</span>
              {selected.categoryName ? (
                <span className="text-muted-foreground ml-2">· {selected.categoryName}</span>
              ) : null}
              {selected.accountCount > 1 ? (
                <span className="text-muted-foreground ml-1">({selected.accountCount} accounts)</span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Select an asset…</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by symbol or name…" />
          <CommandList>
            <CommandEmpty>No assets found.</CommandEmpty>
            <CommandGroup>
              {symbolGroups.map((group) => (
                <CommandItem
                  key={group.symbol}
                  value={`${group.symbol} ${group.categoryName || ''}`}
                  onSelect={() => {
                    onChange(group.symbol, group.ids);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === group.symbol ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{group.symbol}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {group.categoryName || '—'}
                      {group.currency ? ` · ${group.currency}` : ''}
                      {group.accountCount > 1 ? ` · ${group.accountCount} accounts` : ''}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SubscriptionsFullScanCard() {
  const { toast } = useToast();
  const { data } = useSubscriptions({ view: 'active' });
  const mutation = useFullHistoryScan();

  const lastRun = data?.fullScanAt ?? null;

  const handleClick = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: 'Full history scan started',
          description:
            'A 48-month scan is running in the background. Annual and older subscriptions will appear on the Subscriptions page shortly.',
        });
      },
      onError: (err: unknown) => {
        const ax = err as AxiosError<{ error?: string }>;
        toast({
          title: 'Failed to start scan',
          description: ax?.response?.data?.error || ax?.message || 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="font-medium">Subscriptions — full history scan</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Re-runs recurring-charge detection with a 48-month lookback so annual and
          long-dormant subscriptions surface on the Subscriptions page. The nightly
          run and the page's "Scan now" button only look back 6 months.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Last run: {lastRun ? formatRelativeTime(lastRun) : 'never'}
        </p>
      </div>
      {mutation.isPending ? (
        <Button disabled className="w-full sm:w-auto">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Starting…
        </Button>
      ) : (
        <Button onClick={handleClick} className="w-full sm:w-auto">
          <RotateCcw className="h-4 w-4 mr-2" />
          Run full scan
        </Button>
      )}
    </Card>
  );
}

function RefreshFundamentalsButton() {
  const { toast } = useToast();
  const mutation = useRefreshFundamentals();

  // No status polling for this scope — the underlying job runs on the
  // securityMaster queue (concurrency 1, ~2s per symbol). The button
  // disables only while the enqueue HTTP call is in flight; afterwards
  // it's clickable again, but BullMQ serializes duplicate enqueues so
  // the worst-case impact of a re-click is one extra queued run.
  const handleClick = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: 'Refresh started',
          description: 'Stock fundamentals will update over the next few minutes. Reload the Equity Analysis page once complete.',
        });
      },
      onError: (err: unknown) => {
        const ax = err as AxiosError<{ error?: string }>;
        toast({
          title: 'Failed to start refresh',
          description: ax?.response?.data?.error || ax?.message || 'Unknown error',
          variant: 'destructive',
        });
      },
    });
  };

  if (mutation.isPending) {
    return (
      <Button disabled className="w-full sm:w-auto">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Starting…
      </Button>
    );
  }
  return (
    <Button onClick={handleClick} className="w-full sm:w-auto">
      <RotateCcw className="h-4 w-4 mr-2" />
      Refresh fundamentals
    </Button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function MaintenanceTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: status, isLoading: statusLoading, isError: statusError } = useRebuildStatus();
  // Assets for the single-asset picker come from the rebuild status
  // response itself — the backend reads id/symbol/currency/category.name
  // direct from the DB, no price fetching. We deliberately do NOT call
  // `usePortfolioItems()` here because that endpoint kicks off a live
  // price fetch per asset (40+ HTTP calls to TwelveData) just to
  // populate a dropdown.
  const portfolioItems = status?.assets ?? [];
  const trigger = useTriggerRebuild();

  // Per-section local state
  const [scopedDate, setScopedDate] = useState<string>('');
  const [selectedAssetSymbol, setSelectedAssetSymbol] = useState<string | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  const runTrigger = (scope: RebuildScope, payload?: { earliestDate?: string; portfolioItemIds?: number[] }) => {
    trigger.mutate(
      { scope, payload },
      {
        onSuccess: () => {
          toast({ title: `${SCOPE_LABEL[scope]} rebuild started` });
        },
        onError: (err: unknown) => {
          // 409 response body carries { error, scope, ttlSeconds }
          const ax = err as AxiosError<{ error?: string; ttlSeconds?: number }>;
          const status = ax?.response?.status;
          const data = ax?.response?.data;
          if (status === 409) {
            toast({
              title: 'Rebuild already running',
              description: `Try again in ${formatTtl(data?.ttlSeconds ?? null)}.`,
              variant: 'destructive',
            });
            return;
          }
          toast({
            title: 'Failed to start rebuild',
            description: data?.error || ax?.message || 'Unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  if (statusError) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Unable to load rebuild status</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Check your connection and refresh the page. If the problem
              persists, check that the backend is reachable.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* ─── Intro card ────────────────────────────────────────────────── */}
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <Database className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Data maintenance</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Manually trigger background rebuilds when analytics or portfolio
              data looks stale. Each scope has a 1-hour single-flight lock to
              prevent overlapping runs.
            </p>
          </div>
        </div>
      </Card>

      {/* ─── Full analytics ────────────────────────────────────────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">Rebuild all analytics</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Recomputes every <code>AnalyticsCacheMonthly</code> and
            <code> TagAnalyticsCacheMonthly</code> row from current transaction
            data. Use when cached monthly totals disagree with the transactions
            list (the classic "March expenses not updating" symptom). Does
            <strong> not</strong> touch portfolio valuations.
          </p>
        </div>
        <RebuildButton
          scope="full-analytics"
          status={status}
          isPending={trigger.isPending && trigger.variables?.scope === 'full-analytics'}
          disabled={statusLoading}
          onClick={() => runTrigger('full-analytics')}
          label="Rebuild analytics"
        />
      </Card>

      {/* ─── Full rebuild (everything) ─────────────────────────────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">Full rebuild</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Runs the full pipeline end-to-end: re-derives portfolio items from
            transactions, rebuilds cash holdings, runs a full analytics rebuild,
            then revalues every asset (including debt and loan processors).
            Heaviest option — expect 5-30 minutes depending on history size.
          </p>
          <p className="text-sm text-muted-foreground mt-1">{t('maintenance.fullRebuildSecuritiesStep')}</p>
        </div>
        <RebuildButton
          scope="full-portfolio"
          status={status}
          isPending={trigger.isPending && trigger.variables?.scope === 'full-portfolio'}
          disabled={statusLoading}
          onClick={() => runTrigger('full-portfolio')}
          label="Run full rebuild"
        />
      </Card>

      {/* ─── Scoped analytics ──────────────────────────────────────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">Rebuild analytics from a date</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Recompute analytics only from the given date onwards. Faster than a
            full analytics rebuild when the issue is localized to recent
            months.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 max-w-xs space-y-1.5">
            <Label htmlFor="scoped-date" className="text-xs">Earliest date</Label>
            <Input
              id="scoped-date"
              type="date"
              value={scopedDate}
              onChange={(e) => setScopedDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <RebuildButton
            scope="scoped-analytics"
            status={status}
            isPending={trigger.isPending && trigger.variables?.scope === 'scoped-analytics'}
            disabled={statusLoading || !scopedDate}
            onClick={() => {
              // Date input returns `YYYY-MM-DD`; convert to full ISO string
              // at start of day UTC so the backend's `new Date(earliestDate)`
              // gets a canonical value.
              runTrigger('scoped-analytics', {
                earliestDate: new Date(`${scopedDate}T00:00:00.000Z`).toISOString(),
              });
            }}
            label="Rebuild from date"
          />
        </div>
      </Card>

      {/* ─── Refresh my securities data (tenant-scoped, #77) ───────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">{t('maintenance.securityData.title')}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t('maintenance.securityData.description')}</p>
        </div>
        <RebuildButton
          scope="security-data"
          status={status}
          isPending={trigger.isPending && trigger.variables?.scope === 'security-data'}
          disabled={statusLoading}
          onClick={() => runTrigger('security-data')}
          label={t('maintenance.securityData.button')}
        />
      </Card>

      {/* ─── Refresh fundamentals — GLOBAL SecurityMaster table ────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">{t('maintenance.globalFundamentals.title')}</h3>
          <p className="text-sm text-muted-foreground mt-1">{t('maintenance.globalFundamentals.description')}</p>
        </div>
        <RefreshFundamentalsButton />
      </Card>

      {/* ─── Subscriptions — full history scan ─────────────────────────── */}
      <SubscriptionsFullScanCard />

      {/* ─── Single asset ──────────────────────────────────────────────── */}
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="font-medium">Rebuild a single asset</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Re-value one portfolio asset's full price history and holdings.
            Useful when a corporate action, manual value edit, or source-data
            fix only affected one position.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs">Asset</Label>
            {statusLoading ? (
              <div className="h-10 rounded-md border bg-muted animate-pulse" />
            ) : (
              <AssetPicker
                items={portfolioItems}
                value={selectedAssetSymbol}
                onChange={(sym, ids) => {
                  setSelectedAssetSymbol(sym);
                  setSelectedAssetIds(ids);
                }}
              />
            )}
          </div>
          <RebuildButton
            scope="single-asset"
            status={status}
            isPending={trigger.isPending && trigger.variables?.scope === 'single-asset'}
            disabled={statusLoading || selectedAssetIds.length === 0}
            onClick={() => {
              if (selectedAssetIds.length === 0) return;
              runTrigger('single-asset', { portfolioItemIds: selectedAssetIds });
            }}
            label="Rebuild asset"
          />
        </div>
      </Card>

      {/* ─── Progress & history: moved to Settings → Processing (#100) ─── */}
      <Card className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-2">
          <History className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <p className="text-sm text-muted-foreground">{t('activity.maintenance.seeProgressHint')}</p>
        </div>
        <Button asChild variant="outline" size="sm" className="shrink-0">
          <Link to="/settings?tab=processing">{t('activity.maintenance.seeProgress')}</Link>
        </Button>
      </Card>

    </div>
  );
}
